import {Component, EventEmitter, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {CommonModule} from "@angular/common";
import {Session} from "../../services/Session";
import {ActivatedRoute, Router} from "@angular/router";
import {PageTitleService} from "../../services/PageTitle";
import {LoadingService} from "../../services/LoadingService";
import {StateService} from "../../services/StateService";
import {ToastService} from "../../services/Toast.service";
import {SwitchBoard} from "../../services/SwitchBoard";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {ManageTaxStrategiesServices} from "../../tpiq-services/manage-tax-strategies.service";
import {takeUntil} from "rxjs/operators";
import { forkJoin } from 'rxjs';
import {environment} from "../../../environments/environment";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {State} from "../../models/State";
import {FormsModule} from "@angular/forms";
import {TrackerPaginatedTableComponent} from "../../tables/tracker-paginated-table/tracker-paginated-table.component";
import {CreateOrUpdateClientComponent} from "./create-or-update-client/create-or-update-client.component";
import {DashboardUploadCreatePlanComponent} from "../core-focus/dashboard-upload-create-plan/dashboard-upload-create-plan.component";
import {ClientLabelsSearchComponent} from "../../search/client-labels-search/client-labels-search.component";
import {NumeralService} from '../../services/Numeral.service';
import {TpiqSelectDropdownComponent} from "../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FeatureFlagService} from "../../tpiq-services/feature-flag.service";

declare const _: any;
declare const jQuery: any;

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [CommonModule, FormsModule, TrackerPaginatedTableComponent, CreateOrUpdateClientComponent, DashboardUploadCreatePlanComponent,
    ClientLabelsSearchComponent, TpiqSelectDropdownComponent],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ClientsComponent implements OnInit, OnDestroy {
  userTableData: any = {};
  hasUserList = false;
  nextIndex = 0;
  org: string = Session.getOrg();
  user: any = {};
  taxStrategiesSort: any = {};
  isLoading = false;
  orderArray: any = {
    'First Name': 'firstName',
    'Last Name': 'lastName',
    Status: 'status',
    Labels: 'labels',
    'Plan Year' : 'defaultYear',
    Plans: 'planCount',
    'Last Updated By': 'lastUpdatedBy',
    'Last Updated Date': 'lastUpdatedDate',
  };
  orderArrayForDraftClients: any = {
    'First Name': 'firstName',
    'Last Name': 'lastName',
    'Plan Name': 'planName',
    Progress: 'status',
    'Est. Savings': 'potentialSavingsNew',
    'Est. Fee': 'potentialFee',
    'Est. Client ROI' : 'roiFee',
    'Uploaded Date': 'uploadedDate'
  };

  destroy$: EventEmitter<void> = new EventEmitter<void>();
  currentEnvironment: any;
  protectedView = false;
  sortKey: string;
  sortOrder: string;
  clientSideSortKey: string;
  clientSideSortOrder: string;
  clientPortalAccess: boolean;
  searchObj: any = {};
  hasNextSet = false;
  statusTypes: Array<any> = [{id: 'Potential Client', name: 'Potential Client'}, {id: 'Client', name: 'Client'}, {id: 'Former Client', name: 'Former Client'}, {id: 'Closed - Lost', name: 'Closed - Lost'}, {id: 'Custom', name: 'Custom'}];
  reveal: any = {
    addOrEditClient: false,
    customStatus: false,
    showConfirmation: false,
    createPlan: false,
    viewJson: false,
    view1040PDF: false,
    viewQuestionarieDetails: false
  };
  clientCustomStatus = '';
  selectedClientRowData: any;
  editMode = false;
  clientId: any;
  selectedSearchValues: any;
  searchFilters: any;
  searchKey: string;
  previousSearchKey: string = '';
  isReviewPlan = false;
  currentTab = '';
  draftClientDocId: any;
  draftClientDocumentName: any;
  draftPlanId: any;
  draftClientPlanName: any;
  tabNameToLand: any;
  intervalId: any;
  draftProcessingExist: boolean = false;
  ocrStatus: any;
  sortKeyForDraft: string;
  sortOrderForDraft: string;
  ocrViewJsonData: any;
  questionarieData: any;
  chatId: any;
  questionnaireTrack: number = 0;
  isRapMaximized = false;
  questionnaireUtilization = false;

  // Draft Clients bulk selection
  draftActions = [{ name: 'Delete' }];
  selectedDraftRows: any[] = [];
  draftBulkSelectAll = false;
  draftBulkUnselected: any[] = [];
  selectedDraftRowsCount = 0;


  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private titleService: PageTitleService,
    private loadingService: LoadingService,
    private stateService: StateService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private taxPlanningService: TaxPlanningService,
    private taxStrategyServices: ManageTaxStrategiesServices,
    private numeralService: NumeralService,
    private featureFlag: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.titleService.setPageTitle('');
    this.user = Session.getUser();
    this.currentEnvironment = environment;
    this.checkClientPortalAccess();
    if (Session.getTaxPlanningProtectedView()) {
      this.protectedView = true;
    } else {
      this.protectedView = false;
      Session.setTaxPlanningProtectedView(this.protectedView);
    }

    // 868m1tnat: Tax Projections deep-links here with ?rapDoc=<documentId> so
    // its savings figure opens RAP on the right draft. Read once from the
    // snapshot: we always arrive on a fresh navigation, and a subscription
    // would re-fire the auto-open on every later query-param change.
    const requestedRapDoc = this._route.snapshot.queryParams['rapDoc'] || null;
    let firstRouteResolution = true;

    this._route.params.subscribe(params => {
      const isDraftTab = !!(params && params.type === 'draft');

      if (firstRouteResolution) {
        firstRouteResolution = false;
        // The rapDoc is honoured ONLY when the deep link actually lands on
        // the draft tab. Kept unconditionally it would sit here for the
        // component's life and then pop the RAP modal open the first time the
        // Advisor clicked the Draft Clients tab themselves, minutes later,
        // from an action they never took.
        this.pendingRapDocId = isDraftTab ? requestedRapDoc : null;
      }

      if (isDraftTab) {
        this.currentTab = 'Draft Clients';
        this.getDraftClientsList();
      } else {
        this.currentTab = 'Live Clients';
        this.getUsersNextSet(true);
      }
    });

    this.switchBoard.onToggleFeedback.next({
      display: true,
      pageName: 'Clients list page'
    });
    this.switchBoard.onToastConfirm.pipe(takeUntil(this.destroy$)).subscribe(toast => {
      if (toast.data) {
        if (toast.data.type === 'client-deletion') {
          this.deleteClient(toast.data.clientId);
        } else if (toast.data.type === 'draft-client-deletion') {
          this.deleteDraftClient(toast.data.clientId, toast.data.docId);
        } else if (toast.data.type === 'draft-client-bulk-deletion') {
          this.bulkDeleteDraftClients(toast.data.drafts || []);
        }
      }
    });
    this.switchBoard.onClientLabelsAdvSearch.pipe(takeUntil(this.destroy$)).subscribe(search => {
      if (search.selectedValues) {
        this.selectedSearchValues = search.selectedValues || [];
      }
      if (search.filters) {
        this.searchFilters = search.filters || [];
      }
      if (search.searchObj) {
        this.searchObj = search.searchObj;
        this.nextIndex = 0;
        this.getUsersNextSet(true);
      }
    });
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(obj => {
      const prevState = this.stateService.getPrevState();
      if (prevState) {
        this.stateService.pop();
        this._router.navigate([prevState.url]);
      }
    });
    this.intervalId = setInterval(() => {
      if (this.currentTab === 'Draft Clients' && this.draftProcessingExist && !this.reveal.createPlan) {
        this.draftProcessingExist = false;
        this.getDraftClientsList();
      }
    }, 30000);
  }

  checkClientPortalAccess() {
    const accessInfo = Session.getUserAccessInfo();
    if (accessInfo && accessInfo.clientPortalAccess) {
      this.clientPortalAccess = true;
    } else {
      this.clientPortalAccess = false;
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  getUsersNextSet(refreshTable){
    let clientStatus: any;
    const obj: any = {
      fromIndex: this.nextIndex,
    };

    // Only send sort params when we intend the backend to sort.
    if (this.sortKey && this.sortOrder) {
      obj.sortKey = this.sortKey;
      obj.sortOrder = this.sortOrder;
    }

    const normalizedSearch = this.normalizeSearchTerm(this.searchKey);
    if (normalizedSearch) {
      // When searching, request full list and apply filtering client-side across all displayed columns.
      obj.fromIndex = -1;
      // IMPORTANT: do not pass searchKey to backend; backend search behavior differs and can pre-filter incorrectly.
    }
    // When forcing a client-side sort, request full list as well.
    if (this.clientSideSortKey) {
      obj.fromIndex = -1;
    }
    if (!_.isEmpty(this.searchObj)) {
      obj['labels'] = _.map(this.searchObj.selectedLabels, 'id');
      obj['status'] = this.searchObj.status || '';
      clientStatus = this.searchObj.status || '';
    }

    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClients(this.org, this.user.id, obj, this.protectedView, clientStatus || '')
      .subscribe((response) => {
        this.isLoading = false;
        this.loadingService.triggerLoadingEvent(false);
        this.hasUserList = true;
        const normalizedSearch = this.normalizeSearchTerm(this.searchKey);
        let clients = normalizedSearch ? this.filterClientsLocally(response.clients, normalizedSearch) : response.clients;

        // Fallback client-side sort for columns the backend doesn't support.
        if (this.clientSideSortKey) {
          const key = this.clientSideSortKey;
          const order = (this.clientSideSortOrder || '').toLowerCase();
          clients = (clients || []).slice().sort((a: any, b: any) => {
            const av = (a && a[key] !== undefined && a[key] !== null) ? a[key].toString().toLowerCase() : '';
            const bv = (b && b[key] !== undefined && b[key] !== null) ? b[key].toString().toLowerCase() : '';
            if (av < bv) { return order === 'desc' ? 1 : -1; }
            if (av > bv) { return order === 'desc' ? -1 : 1; }
            return 0;
          });
        }

        // When client-side searching or client-side sorting, treat it as a single result set
        if (normalizedSearch || this.clientSideSortKey) {
          this.nextIndex = 0;
          this.hasNextSet = false;
        } else {
          this.nextIndex += response.clients.length;
          this.hasNextSet = response.hasNextPage;
        }

        this.buildUserTableData(clients, refreshTable);
      }, (error) => {
        this.isLoading = false;
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  addNewUser(){
    this.editMode = false;
    this.reveal.addOrEditClient = true;
  }

  handleScroll(event){
    const base = this;
    if (event.isReachingBottom && !base.isLoading && !event.isWindowEvent && this.hasNextSet) {
      base.isLoading = true;
      this.getUsersNextSet(false);
    }
  }

  /** 868m1tnat: the documentId a deep link asked to open, consumed once. */
  pendingRapDocId: string | null = null;

  handleAction($event){
    this.clientId = (this.currentTab === 'Live Clients') ? $event.id : $event.clientId;
    if(this.currentTab === 'Draft Clients') {
      this.chatId = $event.chatId;
      this.questionnaireTrack = $event.questionnaireTrack ? $event.questionnaireTrack : 0;
      this.questionnaireUtilization = $event.questionnaireUtilization ? $event.questionnaireTrack : false;
    } 
    this.draftClientDocumentName = $event.documentName ? $event.documentName : "";

    if ($event.action == 'edit'){
      this.editMode = true;
      this.reveal.addOrEditClient = true;
    } else if ($event.action == 'view'){
      this.loadingService.triggerLoadingEvent(true);
      this.handleRowClick($event);
    } else if ($event.action == 'delete'){
      this.toastService.pop(TOAST_TYPE.confirm, "Please confirm permanent deletion of this client and ALL related plans.", {
        clientId: this.clientId,
        type: 'client-deletion'
      });
    } else if ($event.action == 'switch-customer-view'){
      this.moveToCustomerView($event);
    } else if ($event.action == 'create-plan'){
      this.draftClientDocId = $event.documentId || '';
      this.draftClientPlanName = $event.planName || '';
      this.tabNameToLand = 'clientInfo';
      this.ocrStatus = $event.ocrStatus;
      this.reveal.createPlan = true;
      this.isReviewPlan = false;
    }else if($event.action == 'complete-questionaries'){
      this.draftClientDocId = $event.documentId || '';
      this.draftClientPlanName = $event.planName || '';
      this.draftPlanId = $event.draftPlanId || '';
      this.reveal.createPlan = true;
      this.ocrStatus = $event.ocrStatus;
      this.tabNameToLand = 'questionnaire';
      this.isReviewPlan = true;
    } else if ($event.action == 'select-strategies'){
      this.draftClientDocId = $event.documentId || '';
      this.draftClientPlanName = $event.planName || '';
      this.draftPlanId = $event.draftPlanId || '';
      this.ocrStatus = $event.ocrStatus;
      this.reveal.createPlan = true;
      this.tabNameToLand = 'strategiesInfo';
      this.isReviewPlan = true;
    } else if ($event.action == 'review-strategies'){
      this.draftClientDocId = $event.documentId || '';
      this.draftClientPlanName = $event.planName || '';
      this.draftPlanId = $event.draftPlanId || '';
      this.ocrStatus = $event.ocrStatus;
      this.reveal.createPlan = true;
      this.tabNameToLand = 'review';
      this.isReviewPlan = true;
    } else if ($event.action == 'roi-fee'){
      this.draftClientDocId = $event.documentId || '';
      this.draftClientPlanName = $event.planName || '';
      this.draftPlanId = $event.draftPlanId || '';
      this.ocrStatus = $event.ocrStatus;
      this.reveal.createPlan = true;
      this.tabNameToLand = 'roiFee';
      this.isReviewPlan = true;
    } else if ($event.action=== 'draft-client-delete') {
      this.toastService.pop(TOAST_TYPE.confirm, "Are you sure you want to delete?", {
        clientId: this.clientId,
        type: 'draft-client-deletion',
        docId: $event.documentId || ''
      }); 
    } else if ($event.action=== 'rap-help-article'){
        window.open('https://meyertax-8719651.hs-sites.com/en/knowledge/why-is-my-1040-upload-failing-or-showing-errors-in-rap', '_blank', 'noopener,noreferrer');
    }
  }

  deleteClient(clientId){
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.deleteClient(this.org, this.user.id, clientId)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Client deleted successfully');
        this.nextIndex = 0;
        this.getUsersNextSet(true);
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to delete client');
      });
  }

  deleteDraftClient(clientId, docId){
    if (clientId && docId) {
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.deleteDraftClient(this.org, this.user.id, clientId, docId)
        .subscribe(response => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.success, 'Client deleted successfully');
          this.draftProcessingExist = false;
          this.getDraftClientsList();
        }, error => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.error, 'Failed to delete client');
        });
    } else {
      this.toastService.pop(TOAST_TYPE.error, 'Failed to delete client');
    }
  }

  sortByColumn(obj){
    this.taxStrategiesSort[obj.column] = obj.order;
    this.resetSortOrder(this.taxStrategiesSort, this.userTableData.columns, obj.column);
    if (this.currentTab === 'Live Clients') {
      this.nextIndex = 0;
      const resolved = this.resolveSortKey(obj.column);

      // Backend appears to not support sorting by lastUpdatedBy (returns empty set), so sort client-side.
      if (resolved === 'lastUpdatedBy') {
        this.clientSideSortKey = 'lastUpdatedBy';
        this.clientSideSortOrder = obj.order;
        this.sortKey = '';
        this.sortOrder = '';
      } else {
        this.clientSideSortKey = '';
        this.clientSideSortOrder = '';
        this.sortKey = resolved;
        this.sortOrder = obj.order;
      }

      this.getUsersNextSet(true);
    } else {
      this.sortKeyForDraft = this.resolveSortKey(obj.column);
      this.sortOrderForDraft = obj.order;
      this.getDraftClientsList();
    }
  }

  resetSortOrder(sortOrder, columns, column) {
    _.each(columns, function(col){
      if (col.name !== column) {
        sortOrder[col.name] = '';
      }
    });
  }

  buildUserTableData(data, refreshTable){
    const base = this;
    this.userTableData.options = {
      height: '66vh',
      selectorString: 'id',
      showSearch: false,
    };
    this.userTableData.columns = [];

    if (this.currentTab === 'Draft Clients') {
      // Add select column for bulk actions (mirrors Strategy List)
      this.userTableData.columns.push({
        name: 'selectCol',
        title: `<input type='checkbox' class='global-checkbox'/>`,
        type: 'html',
        sortable: false,
      });
      this.userTableData.columns = this.userTableData.columns.concat(
        this.formatColumnsForTable(this.orderArrayForDraftClients)
      );
    } else {
      this.userTableData.columns = this.formatColumnsForTable(this.orderArray);
    }

    this.userTableData.columns.push({
      name: 'actions',
      classes: 'overflow-css float-right',
      title: '',
      type: 'html',
      sortable: false
    });
    if (refreshTable) {
      this.userTableData.rows = [];
    } else {
      this.userTableData.rows = this.userTableData.rows || [];
    }
    const rows = base.userTableData.rows || [];
    data.forEach(function(tax){
      const row = base.createTableRow(tax);
      rows.push(row);
    });
    this.userTableData = {...this.userTableData, rows};
    this.handleSortOrder(this.taxStrategiesSort, this.userTableData.columns);
    if (refreshTable) {
      this.hasUserList = false;
    }
    this.loadingService.triggerLoadingEvent(false);
    base.hasUserList = true;
  }

  handleDraftSelect(event: any) {
    // Handle header checkbox: event is boolean (true = select all, false = deselect all)
    if (typeof event === 'boolean') {
      this.updateDraftSelection(event);
      return;
    }

    // Handle individual row selection: event is a row object
    if (event && typeof event === 'object') {
      this.handleDraftRowSelection(event);
      return;
    }

    // Handle null/undefined: deselect all
    if (!event) {
      this.resetDraftSelection();
    }
  }

  updateDraftSelection(state: boolean): void {
    if (state) {
      // Select all: set bulk select flag and get all rows
      this.draftBulkUnselected = [];
      this.selectedDraftRows = [];
      this.draftBulkSelectAll = true;
      const allRows = (this.userTableData?.rows || []).filter(r => !r.isTotalRow);
      this.selectedDraftRows = allRows;
      this.selectedDraftRowsCount = allRows.length;
      
      // Check all checkboxes in the table (use a selector that finds tables within this component)
      setTimeout(() => {
        jQuery('.clients-data table').find('tbody tr input.checkbox').each(function(idx: number, cbox: any) {
          jQuery(cbox).prop('checked', true);
        });
      }, 0);
    } else {
      // Deselect all
      this.resetDraftSelection();
      
      // Uncheck all checkboxes in the table
      setTimeout(() => {
        jQuery('.clients-data table').find('tbody tr input.checkbox').each(function(idx: number, cbox: any) {
          jQuery(cbox).prop('checked', false);
        });
      }, 0);
    }
  }

  handleDraftRowSelection(row: any): void {
    if (!row) {
      return;
    }

    const rowId = row.id || row.documentId;
    if (!rowId) {
      return;
    }

    const idx = _.findIndex(this.selectedDraftRows, (r: any) => (r.id || r.documentId) === rowId);
    
    if (idx === -1) {
      // Row not selected, add it
      this.selectedDraftRows.push(row);
      
      // If we're in bulk select mode, check if we need to update the header checkbox
      if (this.draftBulkSelectAll) {
        // Remove from unselected list if it was there
        const unselectedIdx = _.findIndex(this.draftBulkUnselected, (r: any) => (r.id || r.documentId) === rowId);
        if (unselectedIdx !== -1) {
          this.draftBulkUnselected.splice(unselectedIdx, 1);
        }
        this.checkAllDraftSelected();
      }
    } else {
      // Row already selected, remove it
      this.selectedDraftRows.splice(idx, 1);
      
      // If we're in bulk select mode, add to unselected list
      if (this.draftBulkSelectAll) {
        this.draftBulkUnselected.push(row);
        // Uncheck the header checkbox since not all are selected
        jQuery('input.global-checkbox').prop('checked', false);
      }
      
      // If no rows selected, reset bulk selection
      if (this.selectedDraftRows.length === 0) {
        this.resetDraftSelection();
      }
    }
    
    this.selectedDraftRowsCount = this.selectedDraftRows.length;
  }

  checkAllDraftSelected(): void {
    const allRows = (this.userTableData?.rows || []).filter(r => !r.isTotalRow);
    const totalCount = allRows.length;
    const selectedCount = this.selectedDraftRows.length;
    
    // If all rows are selected (accounting for unselected ones), check the header checkbox
    if (totalCount > 0 && selectedCount === (totalCount - this.draftBulkUnselected.length)) {
      jQuery('input.global-checkbox').prop('checked', true);
    }
  }

  getSelectedDraftClients(): Array<{ clientId: string; documentId: string }> {
    if (this.draftBulkSelectAll) {
      const allRows = (this.userTableData?.rows || []).filter(r => !r.isTotalRow);
      const unselectedIds = new Set(
        (this.draftBulkUnselected || []).map((r: any) => r.documentId)
      );
      return allRows
        .filter(r => r.documentId && !unselectedIds.has(r.documentId))
        .map(r => ({ clientId: r.clientId, documentId: r.documentId }));
    }

    return (this.selectedDraftRows || [])
      .filter(r => r.clientId && r.documentId)
      .map(r => ({ clientId: r.clientId, documentId: r.documentId }));
  }

  resetDraftSelection() {
    this.selectedDraftRows = [];
    this.draftBulkSelectAll = false;
    this.draftBulkUnselected = [];
    this.selectedDraftRowsCount = 0;
  }

  applyDraftAction(action: any) {
    if (!action || action.name !== 'Delete') {
      return;
    }

    const selected = this.getSelectedDraftClients();
    if (!selected.length) {
      this.toastService.pop(TOAST_TYPE.error, 'Please select at least one draft client');
      return;
    }

    this.toastService.pop(
      TOAST_TYPE.confirm,
      `Are you sure you want to delete ${selected.length} item(s)?`,
      {
        type: 'draft-client-bulk-deletion',
        drafts: selected,
      }
    );
  }

  bulkDeleteDraftClients(drafts: Array<{ clientId: string; documentId: string }>) {
    if (!drafts || !drafts.length) {
      return;
    }

    this.loadingService.triggerLoadingEvent(true);

    const deleteRequests = drafts.map(d =>
      this.taxPlanningService.deleteDraftClient(
        this.org,
        this.user.id,
        d.clientId,
        d.documentId
      )
    );

    forkJoin(deleteRequests).subscribe({
      next: () => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Draft clients deleted successfully');
        this.resetDraftSelection();
        this.draftProcessingExist = false;
        this.getDraftClientsList();
      },
      error: () => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to delete selected draft clients');
      },
    });
  }

  createTableRow(user) {
    const base = this;
    const row: any = {};
    if (this.currentTab === 'Draft Clients') {
      // Use documentId as the selector key for bulk selection
      row.id = user.documentId || user.id;
      // Checkbox cell for bulk selection
      row.selectCol = `<input type='checkbox' class='checkbox'/>`;
    }
    _.each(user, (value, key) => {
      if (this.currentTab === 'Draft Clients') {
        if (key == 'status') {
          let progressString = ``;
          if (user[key] === 'Processing') {
            this.draftProcessingExist = true;
            progressString += `<span><img class="loader-icon" src="./images/dashboard-upload/loader.png"/>Processing</span>`;
          } else if (user[key] === 'OCR Done') {
            if (!user['draftPlanId']) {
              progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
              progressString += `<a class='actionOnId core-focus-create-plan' data-action='create-plan'><i class="material-icons">add</i><u> Create Plan</u></a>`;
            } else if (user['draftPlanId'] && user['suggestions'] && user['suggestions'].length > 0) {
              if(this.featureFlag.isEnabled('enableQuestionnarieInRap')){
              if (user['reviewedStrategies'] && user['reviewedStrategies'].length > 0 && this.validateReviewStrategy(user['reviewedStrategies'])) {
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Discovery</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Selection</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='roi-fee'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> ROI Fee</u></a>`;
              } else {
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Discovery</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Selection</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='review-strategies'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> Review Strategies</u></a>`;
              }
            }else{
              if (user['reviewedStrategies'] && user['reviewedStrategies'].length > 0 && this.validateReviewStrategy(user['reviewedStrategies'])) {
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Selection</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='roi-fee'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> ROI Fee</u></a>`;
              } else {
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Selection</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='review-strategies'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> Review Strategies</u></a>`;
              }
            }
            } else if (user['draftPlanId']) {
              if(this.featureFlag.isEnabled('enableQuestionnarieInRap')){
              if(user['completeQuestionnaire']){
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Discovery</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='select-strategies'><img class="loader-icon" src="./images/core-focus/blue-circle.png"/><u> Selection</u></a>`;

              }else{
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
                progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
                progressString += `<a class='actionOnId core-focus-create-plan' data-action='complete-questionaries'><img class="loader-icon" src="./images/core-focus/blue-circle.png"/><u> Discovery</u></a>`;
              }
            }else{
              progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Client</span>`;
              progressString += `<span class="core-focus-progress-step"><img class="loader-icon" src="../../../images/core-focus/success2.png"/>Tax Summary</span>`;
              progressString += `<a class='actionOnId core-focus-create-plan' data-action='select-strategies'><img class="loader-icon" src="./images/core-focus/blue-circle.png"/><u> Selection</u></a>`;
            }
            }
          } else if (user[key] === 'Error') {
            progressString += `<span style="color: red">! </span><span class="ocr-error">${user['failureReason']}</span> <a class='actionOnId core-focus-create-plan text-nowrap' data-action='rap-help-article'> <span style="text-decoration: underline;">Help</span> <i class="material-icons" style="font-size:16px;vertical-align:middle;display:inline;">open_in_new</i> </a>`;
            if (!user['draftPlanId']) {
              progressString += `<a class='actionOnId core-focus-create-plan' data-action='create-plan'><i class="material-icons text-muted">add</i><u> Create Plan</u></a>`;
            } else if (user['draftPlanId'] && user['suggestions'] && user['suggestions'].length > 0) {
              progressString += `<a class='actionOnId core-focus-create-plan' data-action='review-strategies'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> Review Strategies</u></a>`;
            } else if (user['draftPlanId']) {
              progressString += `<a class='actionOnId core-focus-create-plan' data-action='select-strategies'><img class="loader-icon" src="../../../images/core-focus/blue-circle.png"/><u> Selection</u></a>`;
            }
          }
          row[key] = progressString;
          row['ocrStatus'] = user[key];
        }else if (key === 'potentialSavings' || key === 'potentialSavingsNew'){
          let formattedValue = base.numeralService.format('$0,0', value);
          if (key === 'potentialSavingsNew' && user['savingsExceedTax']) {
            formattedValue += ' <span style="display: inline-block; position: relative;"><i class="icon ion-ios-information-outline savings-tooltip-icon" style="cursor: help;"></i><span class="savings-tooltip-text">Estimated tax savings exceed the prior-year 1040 total tax. This may be correct if taxes are expected to rise. If not, please double-check the plan amount and assumptions.</span></span>';
          }
          row[key] = formattedValue;
        } else if (key === 'potentialFee'){
          row[key] = base.numeralService.format('$0,0', value);
        } else if (key === 'roiFee'){
          row[key] = base.numeralService.format('0,0', value)+ '%';
        } else {
          row[key] = user[key];
        }
      } else {
        if (key === 'status') {
          const obj = _.find(base.statusTypes, {name: user[key]});
          // Column is now `status` so it can be sorted server-side; store the badge object there.
          row[key] = obj ? obj : {id: '', name: user[key]};
          // Keep the raw string for logic checks/actions.
          row.statusValue = user[key];
        } else if (key === 'labels') {
          const labels = user[key];
          let labelStrings = [];
          _.each(labels, function(label){
            labelStrings.push(`<a class='actionOnId' data-action='view'><span class="client-label">${label.label}</span></a>`);
          });
          row[key] = labelStrings.join(', ');
        } else if (key == 'firstName' || key == 'lastName' || key == 'email'){
          if (key == 'email') {
            row['client_email'] = user[key];
          }
          row[key] = `<a class='actionOnId' data-action='view'><span>${user[key]}</span></a>`;
        } else if (key == 'progress'){

          if (user[key] === 'Processing') {
            row[key] = `<span><img class="loader-icon" src="../../../images/dashboard-upload/loader.png"/>Processing</span>`;
          } else if (user[key] === 'Create Plan') {
            row[key] = `<a class='actionOnId core-focus-create-plan' data-action='create-plan'><i class="material-icons">add</i><u> Create Plan</u></a>`;
          } else if (user[key] === 'Review Plan') {
            row[key] = `<a class='actionOnId core-focus-create-plan pointer' data-action='review-plan'><u> Review Plan</u></a>`;
          }
        } else {
          row[key] = user[key];
        }
      }

    });
    row.actions = '';
    if (row['statusValue'] == 'Inactive') {
      row.actions += `<a class='action' data-action='publish'><span class='icon badge je-badge' style="background-color: #F06459 !important;">Inactive</span></a>`;
    }
    if (this.clientPortalAccess && row['enableClientPortal']) {
      row.actions += `<span class='badge-tooltip'><a class='action item-download' data-action='switch-customer-view'><i class='material-icons' style='color:#14B5F0;font-size: 18px;position: relative;top: 5px;'>switch_camera</i><span class='bottomTooltiptext' style='font-size: 13px !important;width: 120px !important;left: 0px !important;'>Login as client</span></a></span>`;
    }
    if (this.currentTab === 'Live Clients') {
      row.actions += `<a class='action' data-action='edit'><i class="material-icons strategy-action-icon">mode_edit</i></a>`;
      row.actions += `<a class='action' data-action='delete'><span class='todo-del'><i class="material-icons delete">delete</i></span></a>`;
    }

    if (this.currentTab === 'Draft Clients') {
      row.actions += `<a class='action' data-action='draft-client-delete'><span class='todo-del'><i class="material-icons delete">delete</i></span></a>`;
    }
    return row;
  }

  formatColumnsForTable(data) {
    const base = this;
    const colsArray = [];
    let orderArray;
    if (typeof data !== 'string') {
      orderArray = data;
    } else {
      orderArray = JSON.parse(data);
    }
    _.each(Object.keys(orderArray), (key) => {
      if (this.currentTab === 'Draft Clients') {
        if (key == 'First Name' || key == 'Last Name' || key == 'Plan Name' || key == 'Uploaded Date' || key == 'Est. Savings' || key == 'Est. Fee' || key == 'Est. Client ROI') {
           let classes = 'pointer zero-overflow draft-clients-columns';
           // Add extra class only for 'Potential Savings'
          if (key == 'Potential Savings') {
            classes += ' potential-savings';
          }
          colsArray.push({name: orderArray[key], title: key, type: 'html', classes: classes, sortable: true});
        }  else if (key == 'Progress') {
          colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow draft-clients-progress-column', sortable: false});
        } else {
          colsArray.push({name: orderArray[key], title: key, type: 'normal', classes: 'zero-overflow', sortable: true});
        }
      } else {
        if (key === 'Status') {
          colsArray.push({name: orderArray[key], title: key, type: 'badge-input-search', 'status-col': true, editable: true, displayName: 'name', full_name: 'name', displayKey: 'name', 'multi-dropdown': false, classes: 'pointer zero-overflow', data: base.statusTypes, placeholder: key, sortable: true});
        } else if (key === 'Email') {
          if (!base.protectedView) {
            colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: false});
          }
        } else if (key == 'First Name' || key == 'Last Name') {
          colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: true});
        } else if (key == 'Labels') {
          colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: false});
        } else if (key == 'Progress') {
          colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: false});
        } else {
          colsArray.push({name: orderArray[key], title: key, type: 'normal', classes: 'zero-overflow', sortable: true});
        }
      }
    });
    return colsArray;
  }

  handleSortOrder(sortOrder, columns) {
    _.each(columns, function(column){
      column.sortType = sortOrder[column.name];
    });
  }

  addState(){
    this.stateService.addState(new State('tax-planning-clients', this._router.url, null, null));
  }

  moveToCustomerView(obj) {
    if (obj && obj.client_email) {
      const emailId = encodeURIComponent(obj.client_email);
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.getCustomerLoginToken(this.org, this.user.id, emailId).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        if (response.token) {
          let org = Session.getOrg();
          window.open('https://' + org + '.taxplaniq.com/login?token=' + response.token + '&org=' + response.org + '&userId=' + response.userId);
        }
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        const message = error.message ? error.message : 'Failed to login as client';
        try {
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e) {
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
    } else {
      this.toastService.pop(TOAST_TYPE.error, 'Email does not exist.');
    }
  }

  showProtectedView() {
    this.protectedView = !this.protectedView;
    this.nextIndex = 0;
    Session.setTaxPlanningProtectedView(this.protectedView);
    this.getUsersNextSet(true);
  }

  handleRowClick($event) {
    this.addState();
    if ($event.client_email == 'demo@taxplaniq.com') {
      this.taxPlanningService.setUserReadOnly(true);
    } else {
      this.taxPlanningService.setUserReadOnly(false);
    }
    this._router.navigate(['tax-planning', 'clients', $event.id, 'overview']);
  }

  // Clicking anywhere on a Live-client row opens it, same as clicking the name link.
  // Draft rows keep their per-cell action links (Create Plan, etc.).
  handleRowSelect($event) {
    if (!$event || this.currentTab !== 'Live Clients') {
      return;
    }
    $event.action = 'view';
    this.handleAction($event);
  }

  optionSelected($event) {
    this.selectedClientRowData = {
      id: $event.row.id,
    };
    if ($event.column == 'status' && $event.entry.name == 'Custom') {
      this.clientCustomStatus = ($event.row.statusValue) ? $event.row.statusValue : '';
      this.reveal.customStatus = true;
    } else {
      if ($event.column == 'status'){
        this.selectedClientRowData.status = $event.entry.name;
      }
      this.updateClient(this.selectedClientRowData);
    }
  }

  updateCustomStatus() {
    this.selectedClientRowData.status = this.clientCustomStatus;
    this.reveal.customStatus = false;
    this.updateClient(this.selectedClientRowData);
  }

  updateClient(clientData) {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.updateClientStatus(this.org, this.user.id, clientData.id, clientData.status || '').subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.taxStrategyServices.clearFirmLabelsData();
      this.taxPlanningService.clearClientData();
      this.toastService.pop(TOAST_TYPE.success, 'Client updated successfully');
      this.nextIndex = 0;
      this.getUsersNextSet(true);
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      try{
        this.toastService.pop(TOAST_TYPE.error, error.message ? error.message : 'Failed to update client');
      } catch (e){
        this.toastService.pop(TOAST_TYPE.error, 'Failed to update client');
      }
    });
  }

  hideReveal(key){
    this.clientCustomStatus = '';
    this.reveal[key] = false;
    if (key == 'customStatus') {
      this.nextIndex = 0;
      this.getUsersNextSet(true);
    }
    if (key == 'createPlan') {
      this.isRapMaximized = false;
      document.body.classList.remove('rap-maximized-active');
      this.isReviewPlan = false;
      this.draftPlanId = '';
      this.draftClientDocId = '';
      this.draftClientPlanName = '';
      this.tabNameToLand = '';
      this.refreshListOfClients();
    }
  }

  refreshListOfClients() {
    this.clientSideSortKey = '';
    this.clientSideSortOrder = '';
    this.reveal.addOrEditClient = false;
    if (this.currentTab === 'Live Clients') {
      this.nextIndex = 0;
      this.getUsersNextSet(true);
    } else {
      this.draftProcessingExist = false;
      this.getDraftClientsList();
    }
  }

  removeSearchItem(item, index) {
    this.searchFilters.splice(index, 1);
    this.switchBoard.onSearchLabelDelete.next({filters: this.searchFilters, searchItem: item});
  }

  onSearchInputChange() {
    // Automatically reset table when search input becomes empty, but only if we were previously filtering
    const normalized = this.normalizeSearchTerm(this.searchKey);
    const previousNormalized = this.normalizeSearchTerm(this.previousSearchKey);
    
    // Only reset if we're transitioning from filtered to empty (not if already showing default)
    if (!normalized && previousNormalized) {
      this.filterData();
    }
    // Don't update previousSearchKey here - only update it when filterData() is called (Enter pressed)
  }

  filterData() {
    // Normalize in-place so UI state and subsequent calls behave consistently
    const normalized = this.normalizeSearchTerm(this.searchKey);
    
    // If search is cleared, reset to empty string and refresh table
    if (!normalized) {
      this.searchKey = '';
    } else {
      this.searchKey = normalized;
    }

    // Update previous search key to track state
    this.previousSearchKey = this.searchKey;

    if (this.currentTab === 'Live Clients') {
      this.nextIndex = 0;
      this.hasNextSet = false;
      this.getUsersNextSet(true);
    } else {
      this.draftProcessingExist = false;
      this.getDraftClientsList();
    }
  }


  selectClientsType(tab) {
    this.currentTab = tab;
    if (tab === 'Live Clients') {
      this.nextIndex = 0;
      this.getUsersNextSet(true);
    } else {
      this.draftProcessingExist = false;
      this.getDraftClientsList();
    }
  }

  /**
   * 868m1tnat: open RAP on the row a deep link named.
   *
   * RAP is a modal, never a route: it opens only through handleAction, and it
   * needs in-memory inputs that are not in the URL. So a link cannot open it
   * directly. This finds the row and fires the same event a click on the row's
   * action link fires.
   *
   * Keyed on documentId rather than clientId because a client can hold several
   * uploads, and the modal is scoped to one document.
   *
   * Three things this deliberately does:
   *
   *  - Clears pendingRapDocId FIRST, so the 30s poll cannot reopen the modal
   *    after the advisor closes it. The poll is already suppressed while the
   *    modal is open, but not after it closes.
   *  - Does nothing when the row is not 'OCR Done'. A row still Processing has
   *    no plan to create, and one in Error has nothing to show, so the advisor
   *    is better left on the list where the status is visible.
   *  - Picks the action from draftPlanId only, rather than re-deriving the full
   *    progress ladder. Duplicating that ladder here would drift from the
   *    progress cell the moment either changes, and the wizard places the
   *    advisor within the plan itself.
   */
  private autoOpenRapPlan(clients: any[]): void {
    const wanted = this.pendingRapDocId;
    if (!wanted) { return; }
    this.pendingRapDocId = null;

    // The response is asynchronous, so the Advisor may have switched to Live
    // Clients while it was in flight. Opening anyway would show the RAP modal
    // over the wrong tab, and handleAction branches on currentTab to decide
    // whether the client id is $event.id or $event.clientId, so it would read
    // the wrong field too.
    if (this.currentTab !== 'Draft Clients') { return; }

    const row = (clients || []).find(c => c && c.documentId === wanted);
    if (!row) { return; }
    if (row.status !== 'OCR Done') { return; }

    this.handleAction({
      ...row,
      // handleAction reads ocrStatus, which buildUserTableData aliases from
      // status when it renders the progress cell.
      ocrStatus: row.status,
      action: this.rapActionFor(row)
    });
  }

  /**
   * The RAP step a row is actually at.
   *
   * <p>Mirrors the action createTableRow renders on the row itself, so a deep
   * link opens the same step a click would. Deriving it from draftPlanId
   * alone was wrong in one specific way: a draft with a plan but no completed
   * Discovery would open on Selection, and TaxStrategiesInfoComponent's
   * requery depends on the Discovery answers that step has not collected.
   *
   * <p>Warning: createTableRow still renders its own labels and icons from
   * the same conditions. If the RAP step order changes, change both. They
   * were not unified because createTableRow builds an HTML string per branch
   * and this repository's spec suite is 102 failures deep before any change,
   * so a refactor there could not be verified.
   */
  private rapActionFor(row: any): string {
    if (!row['draftPlanId']) {
      return 'create-plan';
    }
    if (row['suggestions'] && row['suggestions'].length > 0) {
      return (row['reviewedStrategies'] && row['reviewedStrategies'].length > 0
        && this.validateReviewStrategy(row['reviewedStrategies']))
        ? 'roi-fee'
        : 'review-strategies';
    }
    if (this.featureFlag.isEnabled('enableQuestionnarieInRap') && !row['completeQuestionnaire']) {
      return 'complete-questionaries';
    }
    return 'select-strategies';
  }

  getDraftClientsList() {
    let clientStatus: any;
    const obj = {
      fromIndex: -1,
      status: 'draft',
      sortKey: this.sortKeyForDraft,
      sortOrder: this.sortOrderForDraft
    };
    const normalizedSearch = this.normalizeSearchTerm(this.searchKey);
    if (normalizedSearch) {
      // IMPORTANT: do not pass searchKey to backend; we filter client-side across all displayed columns.
    }

    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClients(this.org, this.user.id, obj, this.protectedView, 'draft')
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        const normalizedSearch = this.normalizeSearchTerm(this.searchKey);
        const clients = normalizedSearch ? this.filterClientsLocally(response.clients, normalizedSearch) : response.clients;
        this.buildUserTableData(clients, true);
        this.autoOpenRapPlan(clients);
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        const message = error.message ? error.message : 'Failed to clients data';
        try {
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e) {
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
  }

  closeCoreFocusPopup(data) {
    if (data && data.fromTasksSection) {
      this.reveal['createPlan'] = false;
      this.isRapMaximized = false;
      document.body.classList.remove('rap-maximized-active');
      if (this.clientId && data.plan && data.plan.id) {
        this.addState();
        this._router.navigate(['tax-planning', 'client', this.clientId, data.plan.id, data.plan.taxYear, 'client_plan'], {queryParams: {fromListPage: true}});
      }
    } else {
      this.hideReveal('createPlan');
      this.isRapMaximized = false;
      document.body.classList.remove('rap-maximized-active');
    }
  }

  toggleRapMaximize() {
    this.isRapMaximized = !this.isRapMaximized;
    // Add/remove class on body for spinner z-index
    if (this.isRapMaximized) {
      document.body.classList.add('rap-maximized-active');
    } else {
      document.body.classList.remove('rap-maximized-active');
    }
  }

  handleOcrData(data: any) {
   this.ocrViewJsonData = data;
  }

  handleQuestionarieData(data: any) {
     this.questionarieData = data;
  }
  
  private resolveSortKey(rawColumn: any): string {
    // Some table components emit the column title; others emit the column field name.
    // Normalize to the field name we want to send to the backend.
    const col = (rawColumn === null || rawColumn === undefined) ? '' : rawColumn.toString();

    // If the emitted value is a display title, map it to the configured field name.
    const mapped = (this.currentTab === 'Draft Clients')
      ? (this.orderArrayForDraftClients[col] || col)
      : (this.orderArray[col] || col);

    // Backend expects `status` (not `statusName`), but the UI used to sort on `statusName`.
    if (mapped === 'statusName') {
      return 'status';
    }

    return mapped;
  }

  private normalizeSearchTerm(term: any): string {
    if (term === null || term === undefined) {
      return '';
    }
    return term.toString().trim().replace(/\s+/g, ' ');
  }

  private rowToSearchText(client: any): string {
    if (!client) {
      return '';
    }

    // Build a display row so we search across what the table actually shows
    const row: any = this.createTableRow(client);

    const stripHtml = (val: any): string => {
      if (val === null || val === undefined) {
        return '';
      }
      return val
        .toString()
        .replace(/<[^>]*>/g, ' ')   // remove HTML tags
        .replace(/&nbsp;/g, ' ')    // basic HTML entity cleanup
        .replace(/\s+/g, ' ')      // collapse whitespace
        .trim();
    };

    const parts: string[] = [];

    // Include all displayed columns for the current tab
    const columnMap = (this.currentTab === 'Draft Clients') ? this.orderArrayForDraftClients : this.orderArray;
    Object.keys(columnMap).forEach((title) => {
      const key = columnMap[title];
      const value = row[key];

      // Handle badge/objects (e.g., statusName)
      if (value && typeof value === 'object') {
        if (value.name) {
          parts.push(stripHtml(value.name));
        } else if (value.label) {
          parts.push(stripHtml(value.label));
        } else {
          parts.push(stripHtml(JSON.stringify(value)));
        }
      } else {
        parts.push(stripHtml(value));
      }
    });

    // Labels are rendered as HTML; make sure they're searchable
    if (row.labels) {
      parts.push(stripHtml(row.labels));
    }

    // Also include combined full name explicitly (supports "first last")
    const first = (client.firstName || '').toString();
    const last = (client.lastName || '').toString();
    const fullName = this.normalizeSearchTerm(`${first} ${last}`);
    if (fullName) {
      parts.push(fullName);
    }

    return this.normalizeSearchTerm(parts.join(' ')).toLowerCase();
  }

  private filterClientsLocally(clients: any[], rawSearch: any): any[] {
    const search = this.normalizeSearchTerm(rawSearch);
    if (!search) {
      return clients || [];
    }

    const tokens = search.toLowerCase().split(' ').filter(Boolean);
    if (tokens.length === 0) {
      return clients || [];
    }

    return (clients || []).filter((c) => {
      const haystack = this.rowToSearchText(c);
      // AND semantics across tokens (supports "first last")
      return tokens.every(t => haystack.indexOf(t) !== -1);
    });
  }

  validateReviewStrategy(strategies) {
    let allReviewed = true;
    _.each(strategies, (str) => {
      if (!str.strategyId || !str.clientId || !str.sinceYear || !str.associatedWith) {
        allReviewed = false;
      }
    });

    return allReviewed;
  }

}